/**
 * Google Business Rating Display Component
 * Shows verified Google rating with stars, review count and link
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Star, ExternalLink } from 'lucide-react';

export function GoogleRatingDisplay({ 
  rating, 
  reviewsCount, 
  mapsUrl,
  isVerified = false,
  variant = 'default', // 'default', 'compact', 'badge'
  className = ''
}) {
  const { t } = useTranslation();

  // Only show if verified and has rating
  if (!isVerified || !rating) {
    return null;
  }

  // Render stars based on rating
  const renderStars = (size = 'h-4 w-4') => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <Star key={i} className={`${size} fill-yellow-400 text-yellow-400`} />
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <Star key={i} className={`${size} fill-yellow-400/50 text-yellow-400`} />
        );
      } else {
        stars.push(
          <Star key={i} className={`${size} text-gray-300`} />
        );
      }
    }
    return stars;
  };

  // Badge variant - minimal, for cards
  if (variant === 'badge') {
    return (
      <div className={`inline-flex items-center gap-1 px-2 py-1 bg-white/90 rounded-full shadow-sm ${className}`}>
        <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
        <span className="text-sm font-semibold text-gray-800">{rating.toFixed(1)}</span>
        {reviewsCount && (
          <span className="text-xs text-gray-500">({reviewsCount})</span>
        )}
      </div>
    );
  }

  // Compact variant - single line
  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="flex items-center gap-1">
          {renderStars('h-3.5 w-3.5')}
        </div>
        <span className="font-semibold text-sm">{rating.toFixed(1)}</span>
        {reviewsCount && (
          <span className="text-sm text-secondary">
            ({reviewsCount} {t('googleRating.reviews')})
          </span>
        )}
        {mapsUrl && (
          <a 
            href={mapsUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-gold hover:underline flex items-center gap-1"
            style={{ color: '#D4A574' }}
          >
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    );
  }

  // Default variant - full display
  return (
    <div className={`flex items-center gap-3 p-3 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl border border-yellow-100 ${className}`}>
      <div className="flex flex-col items-center">
        <span className="text-2xl font-bold text-gray-800">{rating.toFixed(1)}</span>
        <div className="flex items-center gap-0.5 mt-0.5">
          {renderStars('h-3 w-3')}
        </div>
      </div>
      
      <div className="flex-1">
        {reviewsCount && (
          <p className="text-sm text-gray-600">
            {reviewsCount} {t('googleRating.reviews')}
          </p>
        )}
        {mapsUrl && (
          <a 
            href={mapsUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-gold hover:underline flex items-center gap-1 mt-1"
            style={{ color: '#D4A574' }}
            data-testid="google-maps-link"
          >
            {t('googleRating.viewOnGoogle')}
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
      
      {/* Google logo indicator */}
      <div className="flex items-center gap-1 text-xs text-gray-400">
        <svg className="h-4 w-4" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
      </div>
    </div>
  );
}

export default GoogleRatingDisplay;
